import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { Download } from "lucide-react";
import { useParams } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { fallbackLineSwatch, objectSymbolSvg } from "@/features/power_projects/components/MapTab";
import {
  EmptyState,
  PageHeader,
  SectionCard,
  TableShell,
  Th,
} from "@/features/power_projects/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import * as api from "../api";
import { ErrorLine, INPUT_SM } from "../components/common";
import { useRouteTutorial } from "../hooks";

const STATUS_STYLE: Record<
  string,
  { stroke: string; dash?: string; label: string }
> = {
  projektowane: { stroke: "#1d4ed8", label: "projektowane — trasa i działki" },
  istniejace: { stroke: "#8a8a86", label: "istniejące — tylko tło" },
  likwidowane: {
    stroke: "#b8101f",
    dash: "6 4",
    label: "likwidowane — zawiadomienia",
  },
};

/** Ten sam język symboli, co na mapie projektu: kształt punktu mówi, co to za urządzenie
 *  (słup / stacja / złącze), a kolor i kreska linii — napięcie i czy to budowa, czy
 *  demontaż. Dzięki temu instrukcja warstw wygląda jak to, co projektant zobaczy potem
 *  na mapie, a nie jak zestaw identycznych zawijasów. */
function LayerGlyph({ kind, semantic }: { kind: string; semantic: string }) {
  const html = kind === "object" ? objectSymbolSvg(semantic, 18) : fallbackLineSwatch(semantic);
  return (
    <span
      className="inline-flex h-[18px] w-[28px] items-center justify-center"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

const RULES = [
  "Jeden plik DXF (R2010 lub nowszy), współrzędne w układzie projektu (PL-2000, strefa z nagłówka), w metrach — bez przesunięć i bez skalowania.",
  "Warstwy nazywaj DOKŁADNIE jak w tabeli — po nazwie warstwy system rozpoznaje, co jest trasą, a co tłem. Warstwy o innych nazwach są pomijane.",
  "Trasa kablowa i napowietrzna to POLILINIE (LWPOLYLINE / POLYLINE / LINE) na warstwach liniowych. Bloki, splajny i hatch nie są czytane jako trasa.",
  "Słupy, złącza, mufy i stacje to punkty (POINT albo INSERT bloku) na warstwach obiektowych — każdy da działkę z ULDK.",
  "Sieć istniejąca idzie na warstwy „istniejące” — rysuje się jako tło, NIE tworzy działek. Sieć do demontażu na warstwy „likwidowane”.",
  "Po wgraniu system sam odczyta działki z ULDK. Reszta — strony, KW, tytuły — jest do wpisania ręcznie.",
  "Przed wgraniem użyj „Sprawdź plik” — pokaże, które warstwy zostały rozpoznane. Szablon DXF ma już wszystkie warstwy z presetu.",
];

/** Instrukcja warstw DXF dla firmy prowadzącej: co nazwać jak, żeby trasa i działki
 *  weszły do systemu. Obraz zamiast ściany tekstu. */
export function RouteTutorialPage() {
  const { leadershipId } = useParams<{ leadershipId: string }>();
  const user = useAuthStore((s) => s.user);
  const isContractor = user?.role === "contractor";
  const [preset, setPreset] = useState("");
  const { data, isLoading, error } = useRouteTutorial(
    leadershipId,
    preset || null,
  );
  usePageTitle("Instrukcja warstw DXF");
  useEffect(() => {
    if (!preset && data?.presets?.[0]) setPreset(data.presets[0].id);
  }, [data, preset]);
  const back = isContractor
    ? `/contractor/projects/${leadershipId}`
    : `/power-projects/led/${leadershipId}`;
  const presetName = data?.presets.find((p) => p.id === preset)?.name ?? "";

  return (
    <div className="max-w-5xl">
      <PageHeader
        backTo={back}
        backLabel="wróć do projektu"
        title="Jak przygotować rysunek trasy (DXF)"
        subtitle="Liczy się jedno: nazwy warstw. Poniżej lista warstw presetu i to, co z każdej powstaje"
      />
      {error && <ErrorLine error={error} />}
      {isLoading || !data ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4 text-[13px]">
            <label className="inline-flex items-center gap-2">
              <span className="section-label">preset</span>
              <select
                className={clsx(INPUT_SM, "w-auto")}
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
              >
                {data.presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <span className="text-[var(--color-text-secondary)]">
              Układ: <strong>{data.zone_label}</strong>
            </span>
            {preset && leadershipId && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                onClick={() =>
                  api.downloadRouteTemplate(leadershipId, preset, presetName)
                }
              >
                <Download size={13} /> pobierz szablon DXF z tymi warstwami
              </button>
            )}
          </div>
          {data.preset?.description && (
            <p className="text-[12px] text-[var(--color-text-muted)]">
              {data.preset.description}
            </p>
          )}

          {data.layers.length === 0 ? (
            <div className="card">
              <EmptyState>Ten preset nie ma warstw</EmptyState>
            </div>
          ) : (
            <TableShell tableClassName="text-[13px]">
              <thead>
                <tr>
                  <Th>Jak wygląda</Th>
                  <Th>Nazwa warstwy w DXF</Th>
                  <Th>Co to jest</Th>
                  <Th>Rodzaj</Th>
                  <Th>Co z tego powstaje</Th>
                </tr>
              </thead>
              <tbody>
                {data.layers.map((l) => (
                  <tr
                    key={l.layer_name}
                    className="border-t border-[var(--color-border-muted)]"
                  >
                    <td className="px-3 py-1.5">
                      <LayerGlyph kind={l.kind} semantic={l.semantic_type} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 font-mono font-bold">
                      {l.layer_name}
                    </td>
                    <td className="px-3 py-1.5">
                      {l.semantic_label}
                      <span className="block text-[11px] text-[var(--color-text-muted)]">
                        {l.status_label}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5">
                      {l.kind_label}
                    </td>
                    <td className="px-3 py-1.5">{l.what}</td>
                  </tr>
                ))}
              </tbody>
            </TableShell>
          )}

          <div className="flex flex-wrap gap-5 text-[12px]">
            {Object.entries(STATUS_STYLE).map(([k, s]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <svg width="28" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="28"
                    y2="5"
                    stroke={s.stroke}
                    strokeWidth="2.5"
                    strokeDasharray={s.dash}
                  />
                </svg>
                {s.label}
              </span>
            ))}
          </div>

          <SectionCard title="Zasady — krótko">
            <ol className="list-decimal space-y-1.5 pl-5 text-[13px]">
              {RULES.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ol>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
